import { useCallback, useEffect, useRef, useState } from "react";
import { newLinearElement } from "@excalidraw/element";
import { getStrokeWidthByKey } from "@excalidraw/common";
import type { AppClassProperties } from "../../types";
import type { LocalPoint } from "@excalidraw/math";
import { t } from "../../i18n";
import "./ShapeBuilder.scss";

interface ShapeBuilderPopupProps {
  onClose: () => void;
  app: AppClassProperties;
}

type Point = { x: number; y: number };

const CANVAS_W = 600;
const CANVAS_H = 500;
const GRID_SIZE = 20;
const HIT_RADIUS = 12;

const distance = (a: Point, b: Point) =>
  Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

const angleBetween = (a: Point, b: Point, c: Point) => {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mag = Math.sqrt(ab.x ** 2 + ab.y ** 2) * Math.sqrt(cb.x ** 2 + cb.y ** 2);
  if (mag === 0) return 0;
  const rad = Math.acos(Math.max(-1, Math.min(1, dot / mag)));
  return (rad * 180) / Math.PI;
};

const formatDist = (d: number) => (d < 10 ? d.toFixed(1) : Math.round(d).toString());

const findNearest = (points: Point[], cx: number, cy: number, threshold = HIT_RADIUS) => {
  let nearest = -1;
  let best = threshold;
  for (let i = 0; i < points.length; i++) {
    const d = Math.sqrt((points[i].x - cx) ** 2 + (points[i].y - cy) ** 2);
    if (d < best) { best = d; nearest = i; }
  }
  return nearest;
};

export const ShapeBuilderPopup = ({ onClose, app }: ShapeBuilderPopupProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [closed, setClosed] = useState(false);
  const [hoveredPt, setHoveredPt] = useState<number | null>(null);
  const [draggingPt, setDraggingPt] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<Point | null>(null);

  // mutable refs for drag to avoid stale closures
  const pointsRef = useRef<Point[]>([]);
  const dragIdxRef = useRef<number | null>(null);
  const dragOffsetRef = useRef<Point>({ x: 0, y: 0 });

  // keep ref in sync
  useEffect(() => { pointsRef.current = points; }, [points]);

  const getCoords = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: clientX - rect.left - CANVAS_W / 2,
      y: clientY - rect.top - CANVAS_H / 2,
    };
  }, []);

  const addPoint = useCallback((pt: Point) => {
    setPoints((prev) => [...prev, pt]);
  }, []);

  const deletePoint = useCallback((index: number) => {
    setPoints((prev) => {
      if (prev.length <= 1) return [];
      return prev.filter((_, i) => i !== index);
    });
    setClosed(false);
  }, []);

  const clearAll = useCallback(() => {
    setPoints([]);
    setClosed(false);
  }, []);

  // ---- canvas drawing ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_W * dpr;
    canvas.height = CANVAS_H * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    const cx = CANVAS_W / 2;
    const cy = CANVAS_H / 2;

    // grid
    ctx.strokeStyle = "#e8e8e8";
    ctx.lineWidth = 0.5;
    for (let x = cx % GRID_SIZE; x < CANVAS_W; x += GRID_SIZE) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CANVAS_H); ctx.stroke();
    }
    for (let y = cy % GRID_SIZE; y < CANVAS_H; y += GRID_SIZE) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CANVAS_W, y); ctx.stroke();
    }

    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, CANVAS_H); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(CANVAS_W, cy); ctx.stroke();

    if (points.length === 0) return;

    const toC = (p: Point) => ({ x: cx + p.x, y: cy + p.y });

    // lines
    ctx.strokeStyle = "#6965db";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    const allPts = closed ? [...points, points[0]] : points;
    for (let i = 0; i < allPts.length; i++) {
      const p = toC(allPts[i]);
      if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();

    // dotted close hint
    if (!closed && points.length >= 3) {
      const f = toC(points[0]);
      const l = toC(points[points.length - 1]);
      ctx.strokeStyle = "#999";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(l.x, l.y); ctx.lineTo(f.x, f.y); ctx.stroke();
      ctx.setLineDash([]);
    }

    // distances
    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    for (let i = 0; i < allPts.length - 1; i++) {
      const a = toC(allPts[i]);
      const b = toC(allPts[i + 1]);
      const d = distance(allPts[i], allPts[i + 1]);
      ctx.fillStyle = "#333";
      ctx.fillText(`${formatDist(d)}`, (a.x + b.x) / 2, (a.y + b.y) / 2 - 4);
    }

    // angles
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    for (let i = 0; i < points.length; i++) {
      const prev = points[(i - 1 + points.length) % points.length];
      const cur = points[i];
      const nxt = closed
        ? points[(i + 1) % points.length]
        : i < points.length - 1 ? points[i + 1] : null;
      if (!nxt) continue;
      if (!closed && i === 0) continue;
      const ang = angleBetween(prev, cur, nxt);
      const p = toC(cur);
      ctx.fillStyle = "#e53935";
      ctx.font = "bold 11px sans-serif";
      ctx.fillText(`${Math.round(ang)}\u00B0`, p.x + 18, p.y - 18);
    }

    // vertices
    for (let i = 0; i < points.length; i++) {
      const p = toC(points[i]);
      const big = draggingPt === i;
      ctx.beginPath();
      ctx.arc(p.x, p.y, big ? 7 : 5, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? "#e53935" : "#6965db";
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#666";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      ctx.fillText(`${i + 1}`, p.x + 8, p.y - 4);
    }

    // highlight ring
    const hi = draggingPt !== null ? draggingPt : hoveredPt;
    if (hi !== null && points[hi]) {
      const p = toC(points[hi]);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 10, 0, Math.PI * 2);
      ctx.strokeStyle = "#6965db";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // guide line from last point to cursor
    if (points.length > 0 && !draggingPt && !closed && mousePos) {
      const last = toC(points[points.length - 1]);
      const cur = toC(mousePos);
      ctx.strokeStyle = "#6965db";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 6]);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(cur.x, cur.y); ctx.stroke();
      ctx.setLineDash([]);
    }

    if (closed && points.length >= 3) {
      const f = toC(points[0]);
      ctx.fillStyle = "#2e7d32";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Closed", f.x, f.y - 24);
    }
  }, [points, closed, hoveredPt, draggingPt, mousePos]);

  // ---- raw DOM pointer events (avoids React closure issues) ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onDown = (e: PointerEvent) => {
      const c = getCoords(e.clientX, e.clientY);
      const nearest = findNearest(pointsRef.current, c.x, c.y);

      if (nearest !== -1) {
        // check if it's the starting point and polygon can be auto-closed
        if (nearest === 0 && pointsRef.current.length >= 3 && !closed) {
          setClosed(true);
          return;
        }
        // start dragging
        dragIdxRef.current = nearest;
        dragOffsetRef.current = {
          x: c.x - pointsRef.current[nearest].x,
          y: c.y - pointsRef.current[nearest].y,
        };
        setDraggingPt(nearest);
        canvas.setPointerCapture(e.pointerId);
        return;
      }

      if (!closed) addPoint(c);
    };

    const onMove = (e: PointerEvent) => {
      const c = getCoords(e.clientX, e.clientY);
      setMousePos(c);

      if (dragIdxRef.current !== null) {
        const idx = dragIdxRef.current;
        const pts = pointsRef.current;
        const next = [...pts];
        next[idx] = {
          x: c.x - dragOffsetRef.current.x,
          y: c.y - dragOffsetRef.current.y,
        };
        pointsRef.current = next;
        // batch with RAF to avoid React overhead per pixel
        setPoints(next);
        return;
      }

      const nearest = findNearest(pointsRef.current, c.x, c.y);
      setHoveredPt(nearest);
      canvas.style.cursor = nearest !== -1 ? "grab" : "crosshair";
    };

    const onUp = () => {
      if (dragIdxRef.current !== null) {
        dragIdxRef.current = null;
        setDraggingPt(null);
      }
    };

    const onLeave = () => setMousePos(null);

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("pointerleave", onLeave);

    return () => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
    };
  }, [closed, addPoint, getCoords]);

  // right-click delete
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const c = getCoords(e.clientX, e.clientY);
    const nearest = findNearest(pointsRef.current, c.x, c.y);
    if (nearest !== -1) deletePoint(nearest);
  }, [deletePoint, getCoords]);

  // keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === "Delete" || e.key === "Backspace") && hoveredPt !== null) {
        deletePoint(hoveredPt);
      }
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hoveredPt, deletePoint, onClose]);

  const handleAddToCanvas = useCallback(() => {
    if (points.length < 3) return;

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const shapeCx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const shapeCy = (Math.min(...ys) + Math.max(...ys)) / 2;

    const sceneCx = app.state.width / 2 - app.state.scrollX;
    const sceneCy = app.state.height / 2 - app.state.scrollY;

    const localPoints: LocalPoint[] = points.map(
      (p) => [p.x, p.y] as LocalPoint,
    );

    const element = newLinearElement({
      type: "line",
      x: sceneCx - shapeCx,
      y: sceneCy - shapeCy,
      polygon: true,
      points: localPoints,
      strokeColor: app.state.currentItemStrokeColor,
      backgroundColor: app.state.currentItemBackgroundColor,
      fillStyle: app.state.currentItemFillStyle,
      strokeWidth: getStrokeWidthByKey("line", app.state.currentItemStrokeWidthKey),
      strokeStyle: app.state.currentItemStrokeStyle,
      roughness: app.state.currentItemRoughness,
      opacity: app.state.currentItemOpacity,
      roundness: null,
    });

    app.scene.insertElementsAtIndex([element], null);
    app.scene.triggerUpdate();
    onClose();
  }, [points, app, onClose]);

  return (
    <div className="ShapeBuilder__overlay" onClick={onClose}>
      <div className="ShapeBuilder__dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="ShapeBuilder__title">{t("toolBar.shapeBuilder")}</h3>

        <div className="ShapeBuilder__canvas-wrap">
          <canvas
            ref={canvasRef}
            className="ShapeBuilder__canvas"
            style={{ width: CANVAS_W, height: CANVAS_H }}
            onContextMenu={handleContextMenu}
          />
        </div>

        <div className="ShapeBuilder__info">
          <span>Points: {points.length}</span>
          {closed && <span>Shape closed</span>}
          {hoveredPt !== null && (
            <span>Pt {hoveredPt + 1} — drag to move, Del to remove</span>
          )}
        </div>

        <div className="ShapeBuilder__actions">
          <button className="ShapeBuilder__btn" onClick={clearAll} disabled={points.length === 0}>
            Clear
          </button>
          {hoveredPt !== null && (
            <button className="ShapeBuilder__btn" onClick={() => deletePoint(hoveredPt)}>
              Delete point
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="ShapeBuilder__btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="ShapeBuilder__btn ShapeBuilder__btn--primary"
            onClick={handleAddToCanvas}
            disabled={points.length < 3}
          >
            Add to Canvas
          </button>
        </div>
      </div>
    </div>
  );
};
